// MessageSheet — the long-press menu for a single message on mobile.
//
// The message itself lifts: the row that was pressed rises in place
// (`.chat-lift-row--active`, styled below), everything else in the
// timeline quiets, and the reaction capsule and the actions sit directly
// under it. Nothing is copied or redrawn, so the eye follows one element
// from the press to the menu and back. If the menu would not fit under
// the message, the message moves up just far enough, and settles back
// when the menu closes. Tap outside, Escape, or drag down to close.
//
// Presentational: the parent owns the message identity and the Matrix
// verbs. Touch targets are >=44px; motion honours `prefers-reduced-motion`.

import { Copy, Download, Flag, Pencil, Plus, Quote, Trash2 } from "lucide-react";
import {
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

import { useModalLifecycle } from "~/components/Dialog";
import { usePortalRoot } from "~/components/EmbedPortalContext";
import { mobileStrings, QUICK_REACTIONS } from "~/components/mobile/strings";
import { ICON_STROKE } from "~/lib/icon";

const STYLE_ID = "chat-message-sheet-styles";
const ROW_CLASS = "chat-lift-row";
const ACTIVE_CLASS = "chat-lift-row--active";
const STACK_CLASS = "chat-message-sheet-stack";
const ACTIONS_ATTRIBUTE = "data-message-sheet-actions";
const LIFT_ATTRIBUTE = "data-message-lift";
const EDGE_PX = 12;
const GAP_PX = 8;
const DRAG_CLOSE_PX = 72;

function injectStyles(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    .${ROW_CLASS} {
      -webkit-user-select: none;
      user-select: none;
      -webkit-touch-callout: none;
      transition: opacity var(--chat-motion-fast) var(--chat-motion-ease);
    }
    .${ROW_CLASS} > .chat-lift-content {
      transition: transform var(--chat-motion-base, 240ms) var(--chat-motion-ease),
        box-shadow var(--chat-motion-base, 240ms) var(--chat-motion-ease);
    }
    body[${LIFT_ATTRIBUTE}] .${ROW_CLASS}:not(.${ACTIVE_CLASS}) { opacity: 0.3; }
    .${ROW_CLASS}.${ACTIVE_CLASS} { overflow: visible; }
    .${ROW_CLASS}.${ACTIVE_CLASS} > .chat-lift-content {
      transform: translateY(var(--chat-lift-dy, 0px)) scale(1.02);
      background: hsl(var(--background));
      border-radius: var(--chat-radius-lg);
      box-shadow: 0 8px 28px hsl(0 0% 0% / 0.14);
    }
    [data-index]:has(.${ACTIVE_CLASS}) { z-index: 5; }
    @keyframes chat-message-sheet-rise {
      from { transform: translateY(-4px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
    .${STACK_CLASS} {
      -webkit-user-select: none;
      user-select: none;
      -webkit-touch-callout: none;
      animation: chat-message-sheet-rise var(--chat-motion-fast) var(--chat-motion-ease);
    }
    @media (prefers-reduced-motion: reduce) {
      .${ROW_CLASS}, .${ROW_CLASS} > .chat-lift-content { transition: none; }
      .${STACK_CLASS} { animation: none; }
    }
  `;
  document.head.appendChild(style);
}
injectStyles();

export interface MessageSheetProps {
  /** Whether the acting user is the message sender. Gates Edit/Delete. */
  isOwnMessage: boolean;
  /** When false, reaction buttons render disabled (session not ready). */
  canReact: boolean;
  /** Quick-reaction emoji row. Defaults to the brief's set. */
  quickReactions?: readonly string[];
  /** The content element of the pressed row. It lifts in place. */
  anchor?: HTMLElement | null;
  /** Toggle a reaction on the message (quick row or full picker). */
  onReact: (emoji: string) => void;
  /** Open the full emoji picker. */
  onMoreReactions: () => void;
  /** Copy a text message; omitted for attachments. */
  onCopyText?: () => void;
  /** Save an attachment to the device; omitted for text. */
  onSave?: () => void;
  /** Save shows but waits while the attachment is still loading. */
  saveReady?: boolean;
  /** Quote text into the composer; omitted for attachments. */
  onQuote?: () => void;
  /** Edit your own message; omitted where editing is not offered. */
  onEdit?: () => void;
  onDelete: () => void;
  /** Report someone else's message; omitted when it can't be reported. */
  onReport?: () => void;
  /** Close request: Escape, outside tap, drag down, or after an action. */
  onClose: () => void;
}

// The usable band of the page: the visual viewport (it shrinks under the
// on-screen keyboard while the layout viewport does not) minus the safe
// areas, which the scrim carries as padding.
function usableBand(scrim: HTMLElement): { top: number; bottom: number } {
  const viewport = window.visualViewport;
  const top = viewport?.offsetTop ?? 0;
  const bottom = top + (viewport?.height ?? window.innerHeight);
  const style = getComputedStyle(scrim);
  return {
    top: top + (Number.parseFloat(style.paddingTop) || 0),
    bottom: bottom - (Number.parseFloat(style.paddingBottom) || 0),
  };
}

function scrollParent(node: HTMLElement): HTMLElement | null {
  for (let el = node.parentElement; el; el = el.parentElement) {
    const overflow = getComputedStyle(el).overflowY;
    if (overflow === "auto" || overflow === "scroll") return el;
  }
  return null;
}

export function MessageSheet({
  isOwnMessage,
  canReact,
  quickReactions = QUICK_REACTIONS,
  anchor = null,
  onReact,
  onMoreReactions,
  onCopyText,
  onSave,
  saveReady = true,
  onQuote,
  onEdit,
  onDelete,
  onReport,
  onClose,
}: MessageSheetProps) {
  const titleId = useId();
  const portalRoot = usePortalRoot();
  const stackRef = useRef<HTMLDivElement | null>(null);
  const dragStartRef = useRef<number | null>(null);
  const [top, setTop] = useState<number | null>(null);

  // Lift the real row, then place the menu under where it lands. The row
  // moves up only as far as the menu needs, and never above its list.
  // When even that cannot fit, the menu covers the message and its actions
  // scroll inside the visible band, so the last one stays reachable.
  useLayoutEffect(() => {
    const stack = stackRef.current;
    const scrim = stack?.parentElement;
    if (!stack || !scrim) return;
    const row = anchor?.parentElement ?? null;
    let natural: number | null = null;
    const place = () => {
      const band = usableBand(scrim);
      stack.style.maxHeight = `${Math.max(0, band.bottom - band.top - 2 * EDGE_PX)}px`;
      if (!anchor || !row) {
        setTop(null);
        return;
      }
      const menuHeight = stack.getBoundingClientRect().height;
      const limit = band.bottom - EDGE_PX;
      if (natural === null) {
        const rect = anchor.getBoundingClientRect();
        const floor = scrollParent(anchor)?.getBoundingClientRect().top ?? EDGE_PX;
        const overflow = rect.bottom + GAP_PX + menuHeight - limit;
        const dy = overflow > 0 ? -Math.min(overflow, Math.max(0, rect.top - floor)) : 0;
        row.style.setProperty("--chat-lift-dy", `${dy}px`);
        row.classList.add(ACTIVE_CLASS);
        document.body.setAttribute(LIFT_ATTRIBUTE, "");
        natural = rect.bottom + dy + GAP_PX;
      }
      setTop(Math.max(band.top + EDGE_PX, Math.min(natural, limit - menuHeight)));
    };
    place();
    // The action list can grow after opening (Save once its file is ready).
    const resize = typeof ResizeObserver === "function" ? new ResizeObserver(place) : null;
    resize?.observe(stack);
    const viewport = window.visualViewport;
    window.addEventListener("resize", place);
    viewport?.addEventListener("resize", place);
    return () => {
      resize?.disconnect();
      window.removeEventListener("resize", place);
      viewport?.removeEventListener("resize", place);
      if (row) {
        row.classList.remove(ACTIVE_CLASS);
        row.style.removeProperty("--chat-lift-dy");
      }
      document.body.removeAttribute(LIFT_ATTRIBUTE);
    };
  }, [anchor]);

  useModalLifecycle(stackRef, onClose);

  const act = (run: () => void) => () => {
    run();
    onClose();
  };

  const positioned = anchor !== null;
  const stackStyle: CSSProperties = positioned
    ? {
        position: "absolute",
        left: 0,
        right: 0,
        top: top ?? 0,
        visibility: top === null ? "hidden" : "visible",
      }
    : {
        position: "absolute",
        left: 0,
        right: 0,
        bottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)",
      };

  const content = (
    <div
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) dragStartRef.current = event.clientY;
      }}
      onPointerUp={(event) => {
        const start = dragStartRef.current;
        dragStartRef.current = null;
        if (event.target === event.currentTarget && start !== null) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        touchAction: "none",
        paddingTop: "env(safe-area-inset-top, 0px)",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }}
    >
      <div
        ref={stackRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={STACK_CLASS}
        onPointerDown={(event) => {
          // A press inside a scrolling action list is a scroll, not a drag
          // to close.
          const actions =
            event.target instanceof Element
              ? event.target.closest<HTMLElement>(`[${ACTIONS_ATTRIBUTE}]`)
              : null;
          const scrolls = actions !== null && actions.scrollHeight > actions.clientHeight;
          dragStartRef.current = scrolls ? null : event.clientY;
        }}
        onPointerCancel={() => {
          dragStartRef.current = null;
        }}
        onPointerUp={(event) => {
          const start = dragStartRef.current;
          dragStartRef.current = null;
          if (start !== null && event.clientY - start > DRAG_CLOSE_PX) onClose();
        }}
        style={{
          ...stackStyle,
          display: "flex",
          flexDirection: "column",
          gap: GAP_PX,
          alignItems: isOwnMessage ? "flex-end" : "flex-start",
          padding: `0 ${EDGE_PX}px`,
          pointerEvents: "none",
          boxSizing: "border-box",
        }}
      >
        <h2 id={titleId} className="chat-visually-hidden">
          {mobileStrings.messageSheet.label}
        </h2>
        <div
          role="group"
          aria-label={mobileStrings.messageSheet.react}
          style={{
            ...surfaceStyle,
            borderRadius: 9999,
            display: "flex",
            alignItems: "center",
            padding: "0 var(--chat-space-2)",
            maxWidth: "100%",
            flexShrink: 0,
          }}
        >
          {quickReactions.map((emoji) => (
            <ReactionButton
              key={emoji}
              emoji={emoji}
              disabled={!canReact}
              onClick={act(() => onReact(emoji))}
            />
          ))}
          <button
            type="button"
            aria-label={mobileStrings.messageSheet.moreReactions}
            disabled={!canReact}
            onClick={act(onMoreReactions)}
            className="chat-button text-muted-foreground hover:text-foreground"
            style={{ ...reactionButtonStyle, opacity: canReact ? 1 : 0.4 }}
          >
            <Plus size={20} strokeWidth={ICON_STROKE} aria-hidden="true" />
          </button>
        </div>
        <div
          {...{ [ACTIONS_ATTRIBUTE]: "" }}
          style={{
            ...surfaceStyle,
            borderRadius: "var(--chat-radius-lg)",
            display: "flex",
            flexDirection: "column",
            padding: "var(--chat-space-1)",
            width: 240,
            maxWidth: "100%",
            minHeight: 0,
            overflowY: "auto",
            overscrollBehavior: "contain",
            touchAction: "pan-y",
          }}
        >
          {onCopyText && (
            <SheetAction icon={Copy} onClick={act(onCopyText)}>
              {mobileStrings.messageSheet.copyText}
            </SheetAction>
          )}
          {onSave && (
            <SheetAction icon={Download} disabled={!saveReady} onClick={act(onSave)}>
              {mobileStrings.messageSheet.save}
            </SheetAction>
          )}
          {onQuote && (
            <SheetAction icon={Quote} onClick={act(onQuote)}>
              {mobileStrings.messageSheet.quote}
            </SheetAction>
          )}
          {onReport && (
            <SheetAction icon={Flag} onClick={act(onReport)}>
              {mobileStrings.messageSheet.report}
            </SheetAction>
          )}
          {isOwnMessage && onEdit && (
            <SheetAction icon={Pencil} onClick={act(onEdit)}>
              {mobileStrings.messageSheet.edit}
            </SheetAction>
          )}
          {isOwnMessage && (
            <SheetAction icon={Trash2} destructive onClick={act(onDelete)}>
              {mobileStrings.messageSheet.delete}
            </SheetAction>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(content, portalRoot ?? document.body);
}

const surfaceStyle: CSSProperties = {
  background: "hsl(var(--background))",
  border: "1px solid hsl(var(--foreground) / 0.04)",
  boxShadow: "0 8px 28px hsl(0 0% 0% / 0.14)",
  pointerEvents: "auto",
};

const reactionButtonStyle: CSSProperties = {
  width: 40,
  height: 44,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: "none",
  borderRadius: "var(--chat-radius-md)",
  background: "transparent",
  cursor: "pointer",
  padding: 0,
  fontSize: 24,
  lineHeight: 1,
};

function ReactionButton({
  emoji,
  disabled,
  onClick,
}: {
  emoji: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={emoji}
      disabled={disabled}
      onClick={onClick}
      className="chat-button"
      style={{ ...reactionButtonStyle, opacity: disabled ? 0.4 : 1 }}
    >
      {emoji}
    </button>
  );
}

function SheetAction({
  children,
  icon: Icon,
  onClick,
  destructive = false,
  disabled = false,
}: {
  children: ReactNode;
  icon: typeof Copy;
  onClick: () => void;
  destructive?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`chat-button flex min-h-11 w-full items-center justify-between gap-3 rounded-md px-3 text-left text-base font-light outline-none transition-colors disabled:opacity-40 ${
        destructive ? "text-destructive-text" : "text-foreground/90 active:text-foreground"
      }`}
    >
      <span>{children}</span>
      <Icon
        aria-hidden="true"
        size={16}
        strokeWidth={ICON_STROKE}
        className={destructive ? "" : "text-muted-foreground"}
      />
    </button>
  );
}
